// Setup.tsx
import React, { useState, useEffect, use, useTransition } from 'react';
import { Button, Box, Typography, Paper, ButtonBase } from '@mui/material';
import { Customer, } from '../../../contexts/CustomerContext';
import EditableTextInline from '../../editors/EditableTextInline';
import { Entity } from '../../../types/entity';
import { useFetch } from '../../../hooks/useFetch3';
import { api } from '../../../api/entityApi';
import { api as refApi } from '../../../api/refApi';
import { api as customerApi } from '../../../api/customerApi';
import { useTranslation } from 'react-i18next';
import { Option, SelectWidget } from '../../editors/SelectWidget2';
import { usePresetDialogs } from '../../dialog';
import dayjs from 'dayjs';
import { usePushNotifications } from '../../../hooks/usePushNotifications2';
import NotificationPrompt from '../../webhook/NotificationPrompt3';
import { FeedbackForm } from '../../feedback/FeedbackForm';
import { feedbackApi } from '../../../api/feedbackApi';
import { useAuthStore } from '../../../store/useAuthStore3';

const SetupComponent: React.FC = () => {
  const { userId, userName, customerId, subtenantId, appRole, logout, stopUserService } = useAuthStore();
  const [text, setText] = useState('');
  const { data, setData, error, update } = useFetch<Entity[]>(api.get, [], {type: 0});
  const { data: dbCustomer, } = useFetch<Customer[]>(
    customerApi.getCustomer, 
    [], 
    customerId, 
    [],
    (v) => {setText(v[0].legal_name)} );
  const { data: langOptions, } = useFetch<Option[]>(refApi.getLanguage, []);
  const { data: crnOptions, } = useFetch<Option[]>(refApi.getCurrency, []);
  const { i18n, t } = useTranslation();
  const { showConfirm, showInfo, DialogComponent } = usePresetDialogs();
  const [showActions, setShowActions] = useState(false);
  const { subscribe, unsubscribe, subscription, permission, loading } = usePushNotifications();
  const [ showFeedbackForm, setShowFeedbackForm ] = useState(false);

  const handleSave = (newValue: string) => {
    setText(newValue);
    const newData = {...data[0], name: newValue};
    api.put(newData);
    handleSaveField('legal_name', newValue);
  };

  const handleSaveField = (field: string, newValue: string) => {
    customerApi.patch(customerId || 0, field, newValue);
    if (field === 'lang') {
      console.log('Attempting to change language to:', newValue);
      i18n.changeLanguage(newValue);
      dayjs.locale(newValue);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  const handleStopService = async () => { 
    const result = await showConfirm('Ви дійсно бажаєте припинити використання послуг? Нагадуємо, що вся Ваша інформація буде видалена з системи', 'Підтвердження критичної операції');
    if (result) {
      // customerApi.stopUserService(customerId||-1, userId||-1);
      stopUserService();
      await handleLogout();
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>

        {DialogComponent}

        {userId && (
          <Box sx={{ mt: 4, width: '100%' }}>

            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                {t('SetupComponent.owner')}
              </Typography>

              <EditableTextInline
                value={text}
                onSave={handleSave}
                width={200}
                type="text"
                multiline={false}
              />

              {dbCustomer && (
              <SelectWidget
                title={t('language')}
                object={dbCustomer[0]}
                field='lang'
                options={langOptions}
                onSave={handleSaveField}
              />
              )}

              {dbCustomer && (
              <SelectWidget
                title={t('OrderForm.CurrencyLabel')}
                object={dbCustomer[0]}
                field='currency'
                options={crnOptions}
                onSave={handleSaveField}
              />
              )}

              <Button variant='outlined' color='info' onClick={() => setShowFeedbackForm(!showFeedbackForm)} sx={{ mt: 2, mb: 2 }}>
                {showFeedbackForm ? 'Сховати' : "Форма зворотного зв'язку" }
                {/* {showFeedbackForm ? t('Setup.HideFeedbackForm') : t('Setup.ShowFeedbackForm')} */}

              </Button>

              {showFeedbackForm && (
                  <FeedbackForm
                    onSubmit={async (data) => {
                      const result = await feedbackApi.submitFeedback({...data, data: {userName}}); 
                      if (result) {
                        console.log('Feedback submitted:', data);
                        showInfo('Дякуємо за ваш відгук!');
                        setShowFeedbackForm(false);
                      } else {
                        console.error('Failed to submit feedback');
                        showInfo('Помилка при відправці відгуку. Спробуйте ще раз пізніше.');
                        setShowFeedbackForm(false);
                      }
                  }}
                  />
              )}

            {dbCustomer && (
              <>
                <br/>
                <NotificationPrompt onSubscribe={subscribe} onUnsubscribe={unsubscribe}/>

                <Typography 
                  variant="body2" 
                  sx={{ cursor: 'pointer', textDecoration: 'underline', mb: 1, mt: 4, }}
                  onClick={() => setShowActions(!showActions)}
                >
                  {showActions ? t('Setup.HideAdditional') : t('Setup.ShowAdditional')}
                </Typography>

                {/* Скрытый контент */}
                {showActions && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {t('Setup.StopService')}
                    </Typography>
                    <Button variant='outlined' color='info' onClick={handleStopService}>
                      Stop Service
                    </Button>

                    <br/>
                    <br/>
                    <Typography variant="h6" gutterBottom>
                      Technical Info
                    </Typography>
                    <Paper sx={{ p: 0, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
                      <pre>{JSON.stringify({ userId, userName, customerId, subtenantId, appRole }, null, 2)}</pre>
                      Frontend version: {__APP_VERSION__} | build: {__BUILD_TIME__}
                    </Paper>
                  </>
                )}
              </>
            )}
            </Paper>

          </Box>
        )}

        {/* <button onClick={subscribe} disabled={permission === 'denied'}>
          {permission === 'denied'
            ? 'Уведомления заблокированы'
            : 'Подписаться на уведомления'}
        </button> */}
    </Box>
  );
};

export default SetupComponent;